import { EventEmitter } from './EventEmitter';
import { FartType, FART_CONFIGS } from '../entities/farts/FartTypes';
import { FartInventory } from '../entities/farts/FartInventory';

/**
 * HTMLTouchControls - Touch controls using HTML/CSS overlay
 * Completely independent of Phaser camera system
 * 
 * Features:
 * - Dynamic floating joystick (left half of screen)
 * - Fart button with swipe-to-select fart types (right side)
 */
export class HTMLTouchControls extends EventEmitter {
    private joystickContainer!: HTMLDivElement;
    private joystickBase!: HTMLDivElement;
    private joystickKnob!: HTMLDivElement;
    private fartButton!: HTMLDivElement;
    private fartIcon!: HTMLDivElement;
    private ammoIndicator!: HTMLDivElement;
    private typeLabel!: HTMLDivElement;
    private styleElement!: HTMLStyleElement;

    private isJoystickActive: boolean = false;
    private direction: { x: number; y: number } = { x: 0, y: 0 };
    private joystickCenterX: number = 0;
    private joystickCenterY: number = 0;
    private joystickTouchId: number | null = null;

    // Dynamic joystick properties
    private baseX: number = 0;
    private baseY: number = 0;

    private readonly JOYSTICK_RADIUS = 50;
    private readonly MARGIN = 30;

    // Swipe detection for fart type selection
    private fartButtonTouchId: number | null = null;
    private fartButtonTouchStartY: number = 0;
    private fartButtonTouchStartTime: number = 0;
    private readonly SWIPE_THRESHOLD = 30; // pixels
    private readonly SWIPE_MAX_TIME = 300; // ms

    // Inventory reference for type selection
    private inventory: FartInventory | null = null;

    create(): void {
        this.createStyles();
        this.createJoystick();
        this.createFartButton();
        this.setupEventListeners();
    }

    /**
     * Set the inventory reference for fart type selection
     */
    setInventory(inventory: FartInventory): void {
        this.inventory = inventory;
        this.updateFartButtonDisplay();
    }

    private createStyles(): void {
        this.styleElement = document.createElement('style');
        this.styleElement.textContent = `
            .touch-controls {
                position: fixed;
                z-index: 1000;
                pointer-events: auto;
                touch-action: none;
            }
            
            .joystick-container {
                position: fixed;
                top: 0;
                left: 0;
                pointer-events: none;
                display: none;
            }
            
            .joystick-base {
                width: 120px;
                height: 120px;
                border-radius: 50%;
                background: rgba(136, 136, 136, 0.3);
                border: 2px solid rgba(255, 255, 255, 0.5);
                position: absolute;
                transform: translate(-50%, -50%);
                transition: opacity 0.2s;
            }
            
            .joystick-knob {
                width: 60px;
                height: 60px;
                border-radius: 50%;
                background: rgba(255, 255, 255, 0.9);
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                transition: none;
                pointer-events: none;
            }
            
            .fart-button {
                position: fixed;
                bottom: ${this.MARGIN}px;
                right: ${this.MARGIN}px;
                width: 100px;
                height: 100px;
                border-radius: 50%;
                background: rgba(253, 203, 110, 0.9);
                border: 4px solid #e17055;
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                cursor: pointer;
                transition: transform 0.1s, background 0.1s;
                user-select: none;
            }
            
            .fart-button:active {
                transform: scale(0.9);
                background: rgba(241, 196, 15, 1);
            }
            
            .fart-icon {
                font-size: 36px;
                line-height: 1;
            }
            
            .fart-ammo {
                position: absolute;
                top: -8px;
                right: -8px;
                min-width: 24px;
                height: 24px;
                padding: 0 6px;
                border-radius: 12px;
                background: #e74c3c;
                color: white;
                font-size: 14px;
                font-weight: bold;
                font-family: Arial, sans-serif;
                display: flex;
                justify-content: center;
                align-items: center;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            }
            
            .fart-ammo.unlimited {
                background: #27ae60;
            }
            
            .fart-ammo.empty {
                background: #95a5a6;
            }
            
            .fart-type-label {
                position: absolute;
                bottom: -24px;
                left: 50%;
                transform: translateX(-50%);
                background: rgba(0, 0, 0, 0.7);
                color: white;
                font-size: 10px;
                font-family: Arial, sans-serif;
                padding: 2px 8px;
                border-radius: 10px;
                white-space: nowrap;
            }
            
            .swipe-hint {
                position: absolute;
                left: -30px;
                top: 50%;
                transform: translateY(-50%);
                color: rgba(255, 255, 255, 0.6);
                font-size: 20px;
                animation: swipeHint 2s infinite;
            }
            
            @keyframes swipeHint {
                0%, 100% { opacity: 0.3; transform: translateY(-50%); }
                50% { opacity: 1; transform: translateY(-60%); }
            }
        `;
        document.head.appendChild(this.styleElement);
    }

    private createJoystick(): void {
        this.joystickContainer = document.createElement('div');
        this.joystickContainer.className = 'touch-controls joystick-container';

        this.joystickBase = document.createElement('div');
        this.joystickBase.className = 'joystick-base';

        this.joystickKnob = document.createElement('div');
        this.joystickKnob.className = 'joystick-knob';

        this.joystickBase.appendChild(this.joystickKnob);
        this.joystickContainer.appendChild(this.joystickBase);
        document.body.appendChild(this.joystickContainer);
    }

    private createFartButton(): void {
        this.fartButton = document.createElement('div');
        this.fartButton.className = 'touch-controls fart-button';

        // Icon container
        this.fartIcon = document.createElement('div');
        this.fartIcon.className = 'fart-icon';
        this.fartIcon.innerHTML = '💨';

        // Ammo indicator
        this.ammoIndicator = document.createElement('div');
        this.ammoIndicator.className = 'fart-ammo unlimited';
        this.ammoIndicator.textContent = '∞';

        // Type label
        this.typeLabel = document.createElement('div');
        this.typeLabel.className = 'fart-type-label';
        this.typeLabel.textContent = 'Basic';

        // Swipe hint
        const swipeHint = document.createElement('div');
        swipeHint.className = 'swipe-hint';
        swipeHint.innerHTML = '↕';

        this.fartButton.appendChild(this.fartIcon);
        this.fartButton.appendChild(this.ammoIndicator);
        this.fartButton.appendChild(this.typeLabel);
        this.fartButton.appendChild(swipeHint);
        document.body.appendChild(this.fartButton);
    }

    /**
     * Update the fart button display based on selected type
     */
    updateFartButtonDisplay(): void {
        if (!this.inventory) return;

        const info = this.inventory.getSelectionInfo();
        const config = FART_CONFIGS[info.type];

        // Update icon
        this.fartIcon.innerHTML = config.icon;

        // Update ammo indicator
        if (info.type === FartType.BASIC) {
            this.ammoIndicator.className = 'fart-ammo unlimited';
            this.ammoIndicator.textContent = '∞';
        } else if (info.ammo > 0) {
            this.ammoIndicator.className = 'fart-ammo';
            this.ammoIndicator.textContent = info.ammo.toString();
        } else {
            this.ammoIndicator.className = 'fart-ammo empty';
            this.ammoIndicator.textContent = '0';
        }

        // Update type label
        this.typeLabel.textContent = config.name;

        // Update button color based on type
        const colorHex = '#' + config.color.toString(16).padStart(6, '0');
        this.fartButton.style.background = `${colorHex}cc`;
        this.fartButton.style.borderColor = colorHex;
    }

    private setupEventListeners(): void {
        // Global touch start (for spawning joystick)
        document.addEventListener('touchstart', (e) => {
            if (this.isJoystickActive) return;

            for (let i = 0; i < e.changedTouches.length; i++) {
                const touch = e.changedTouches[i];
                const isLeftHalf = touch.clientX < window.innerWidth / 2;

                if (isLeftHalf) {
                    e.preventDefault();
                    this.spawnJoystick(touch);
                    break;
                }
            }
        }, { passive: false });

        document.addEventListener('touchmove', (e) => {
            if (!this.isJoystickActive || this.joystickTouchId === null) return;

            for (let i = 0; i < e.changedTouches.length; i++) {
                if (e.changedTouches[i].identifier === this.joystickTouchId) {
                    e.preventDefault();
                    this.handleJoystickMove(e.changedTouches[i]);
                    break;
                }
            }
        }, { passive: false });

        document.addEventListener('touchend', (e) => {
            if (!this.isJoystickActive || this.joystickTouchId === null) return;

            for (let i = 0; i < e.changedTouches.length; i++) {
                if (e.changedTouches[i].identifier === this.joystickTouchId) {
                    this.hideJoystick();
                    break;
                }
            }
        });

        // Fart button with swipe detection
        this.fartButton.addEventListener('touchstart', (e) => {
            e.preventDefault();
            const touch = e.changedTouches[0];
            this.fartButtonTouchId = touch.identifier;
            this.fartButtonTouchStartY = touch.clientY;
            this.fartButtonTouchStartTime = Date.now();
            this.emit('fart-start');
        });

        this.fartButton.addEventListener('touchmove', (e) => {
            // Track for swipe detection (handled in touchend)
            e.preventDefault();
        }, { passive: false });

        this.fartButton.addEventListener('touchend', (e) => {
            e.preventDefault();

            // Check for swipe
            const touch = Array.from(e.changedTouches).find(
                t => t.identifier === this.fartButtonTouchId
            );

            if (touch && this.inventory) {
                const deltaY = this.fartButtonTouchStartY - touch.clientY;
                const deltaTime = Date.now() - this.fartButtonTouchStartTime;

                if (Math.abs(deltaY) > this.SWIPE_THRESHOLD && deltaTime < this.SWIPE_MAX_TIME) {
                    // It's a swipe! Cancel the fart and switch type
                    this.emit('fart-cancel');

                    if (deltaY > 0) {
                        // Swipe UP - next type
                        this.inventory.selectNext();
                    } else {
                        // Swipe DOWN - previous type
                        this.inventory.selectPrevious();
                    }

                    this.updateFartButtonDisplay();
                    this.emit('fart-type-changed', this.inventory.getSelectedType());
                } else {
                    // Not a swipe - release the fart
                    this.emit('fart-release');
                }
            } else {
                this.emit('fart-release');
            }

            this.fartButtonTouchId = null;
        });

        this.fartButton.addEventListener('touchcancel', (e) => {
            e.preventDefault();
            this.emit('fart-cancel');
            this.fartButtonTouchId = null;
        });
    }

    private spawnJoystick(touch: Touch): void {
        this.isJoystickActive = true;
        this.joystickTouchId = touch.identifier;

        this.baseX = touch.clientX;
        this.baseY = touch.clientY;
        this.joystickCenterX = this.baseX;
        this.joystickCenterY = this.baseY;

        this.joystickContainer.style.display = 'block';
        this.joystickBase.style.left = `${this.baseX}px`;
        this.joystickBase.style.top = `${this.baseY}px`;

        this.joystickKnob.style.transform = 'translate(-50%, -50%)';
        this.direction = { x: 0, y: 0 };
    }

    private hideJoystick(): void {
        this.isJoystickActive = false;
        this.joystickTouchId = null;
        this.direction = { x: 0, y: 0 };
        this.joystickContainer.style.display = 'none';
        this.joystickKnob.style.transform = 'translate(-50%, -50%)';
    }

    private handleJoystickMove(touch: Touch): void {
        let dx = touch.clientX - this.joystickCenterX;
        let dy = touch.clientY - this.joystickCenterY;
        let distance = Math.sqrt(dx * dx + dy * dy);
        const maxDistance = this.JOYSTICK_RADIUS;

        if (distance > maxDistance) {
            const angle = Math.atan2(dy, dx);
            const desiredCenterX = touch.clientX - Math.cos(angle) * maxDistance;
            const desiredCenterY = touch.clientY - Math.sin(angle) * maxDistance;

            this.baseX = desiredCenterX;
            this.baseY = desiredCenterY;
            this.joystickCenterX = this.baseX;
            this.joystickCenterY = this.baseY;

            this.joystickBase.style.left = `${this.baseX}px`;
            this.joystickBase.style.top = `${this.baseY}px`;

            dx = touch.clientX - this.joystickCenterX;
            dy = touch.clientY - this.joystickCenterY;
            distance = maxDistance;
        }

        if (distance > maxDistance) {
            const ratio = maxDistance / distance;
            dx *= ratio;
            dy *= ratio;
        }

        this.joystickKnob.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`;

        this.direction = {
            x: dx / maxDistance,
            y: dy / maxDistance
        };
    }

    getDirection(): { x: number; y: number } {
        return this.direction;
    }

    isActive(): boolean {
        return this.isJoystickActive;
    }

    onFartStart(callback: () => void): void {
        this.on('fart-start', callback);
    }

    onFartRelease(callback: () => void): void {
        this.on('fart-release', callback);
    }

    onFartCancel(callback: () => void): void {
        this.on('fart-cancel', callback);
    }

    onFartTypeChanged(callback: (type: FartType) => void): void {
        this.on('fart-type-changed', callback);
    }

    destroy(): void {
        this.styleElement?.remove();
        this.joystickContainer?.remove();
        this.fartButton?.remove();
    }
}

